import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import { Link2, Copy, Check, ExternalLink, Loader2, AlertTriangle, Info, Eye, Monitor, Smartphone, X, GripVertical } from 'lucide-react';
import DoctorSidebar from '../components/DoctorSidebar';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableServiceItem } from '../components/SortableServiceItem';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || window.location.origin;

const DoctorLinkInBio = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // State
  const [doctorId, setDoctorId] = useState(null);
  const [currentSlug, setCurrentSlug] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugError, setSlugError] = useState('');
  const [slugFormatError, setSlugFormatError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'

  // Custom bio state
  const [currentBio, setCurrentBio] = useState('');
  const [newBio, setNewBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [bioError, setBioError] = useState('');
  const [bioSuccessMessage, setBioSuccessMessage] = useState('');

  // Reviews visibility state
  const [showReviews, setShowReviews] = useState(true);
  const [isSavingReviews, setIsSavingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [reviewsSuccessMessage, setReviewsSuccessMessage] = useState('');

  // Bio visibility state
  const [showBio, setShowBio] = useState(true);
  const [isSavingBioVisibility, setIsSavingBioVisibility] = useState(false);
  const [bioVisibilityError, setBioVisibilityError] = useState('');
  const [bioVisibilitySuccessMessage, setBioVisibilitySuccessMessage] = useState('');

  // Service visibility state
  const [services, setServices] = useState([]);
  const [serviceVisibility, setServiceVisibility] = useState({});
  const [isSavingServices, setIsSavingServices] = useState(false);
  const [servicesError, setServicesError] = useState('');
  const [servicesSuccessMessage, setServicesSuccessMessage] = useState('');

  // Service ordering state
  const [orderedServices, setOrderedServices] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderSuccessMessage, setOrderSuccessMessage] = useState('');

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch current slug and custom bio
  const fetchSlug = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'doctor' || !token) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch slug
      const slugResponse = await fetchWithTimeout(
        `${API_URL}/doctors/me/slug`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!slugResponse.ok) {
        throw new Error('Failed to fetch slug');
      }

      const slugData = await slugResponse.json();
      setDoctorId(slugData.doctorId);
      setCurrentSlug(slugData.slug || '');
      setNewSlug(slugData.slug || '');

      // Fetch custom bio
      const bioResponse = await fetchWithTimeout(
        `${API_URL}/doctors/me/link-in-bio-bio`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (bioResponse.ok) {
        const bioData = await bioResponse.json();
        setCurrentBio(bioData.linkInBioBio || '');
        setNewBio(bioData.linkInBioBio || '');
      }

      // Fetch reviews visibility setting
      const reviewsResponse = await fetchWithTimeout(
        `${API_URL}/doctors/me/show-reviews`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json();
        setShowReviews(reviewsData.showReviews ?? true);
      }

      // Fetch bio visibility setting
      const bioVisibilityResponse = await fetchWithTimeout(
        `${API_URL}/doctors/me/show-bio`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (bioVisibilityResponse.ok) {
        const bioVisibilityData = await bioVisibilityResponse.json();
        setShowBio(bioVisibilityData.showBio ?? true);
      }

      // Fetch services (after doctorId is set)
      if (slugData.doctorId) {
        const servicesResponse = await fetchWithTimeout(
          `${API_URL}/doctors/${slugData.doctorId}/services`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          },
          15000
        );

        if (servicesResponse.ok) {
          const servicesData = await servicesResponse.json();
          setServices(servicesData.services || []);

          // Initialize visibility state
          const initialVisibility = {};
          (servicesData.services || []).forEach(service => {
            initialVisibility[service.id] = service.showOnLinkInBio ?? true;
          });
          setServiceVisibility(initialVisibility);

          // Initialize ordered services (sorted by displayOrder)
          const sortedServices = [...(servicesData.services || [])].sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setOrderedServices(sortedServices);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to the server.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, token]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'doctor' && token) {
      fetchSlug();
    }
  }, [isAuthenticated, user, token, fetchSlug]);

  // Check slug availability with debounce
  useEffect(() => {
    const checkSlug = async () => {
      const slug = newSlug.trim();

      // Don't check if there's a format error
      if (slugFormatError) {
        setSlugAvailable(null);
        setSlugError('');
        return;
      }

      // Don't check if empty, same as current, or too short
      if (!slug || slug === currentSlug || slug.length < 3) {
        setSlugAvailable(null);
        setSlugError('');
        return;
      }

      setIsChecking(true);
      setSlugError('');

      try {
        const response = await fetchWithTimeout(
          `${API_URL}/doctors/check-slug/${encodeURIComponent(slug)}`,
          { headers: { 'Content-Type': 'application/json' } },
          10000
        );

        const data = await response.json();
        setSlugAvailable(data.available);
        if (!data.available && data.error) {
          setSlugError(data.error);
        }
      } catch (err) {
        console.error('Error checking slug:', err);
        setSlugAvailable(null);
      } finally {
        setIsChecking(false);
      }
    };

    const debounceTimer = setTimeout(checkSlug, 500);
    return () => clearTimeout(debounceTimer);
  }, [newSlug, currentSlug, slugFormatError]);

  // Save slug (with confirmation for changes)
  const handleSave = async () => {
    const slug = newSlug.trim();

    if (!slug) {
      setSlugError('Please enter a slug');
      return;
    }

    // Check format validation
    const formatError = validateSlugFormat(slug);
    if (formatError) {
      setSlugFormatError(formatError);
      return;
    }

    if (slug === currentSlug) {
      setSuccessMessage('No changes to save');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }

    // Show warning if changing an existing slug
    if (currentSlug && slug !== currentSlug) {
      setShowWarning(true);
      return;
    }

    // Proceed with save
    await performSave(slug);
  };

  // Perform the actual save operation
  const performSave = async (slug) => {
    setShowWarning(false);
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/slug`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ slug })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save slug');
      }

      setCurrentSlug(data.slug);
      setNewSlug(data.slug);
      setSuccessMessage('Your custom URL has been saved!');
      setSlugAvailable(null);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving slug:', err);
      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to the server.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel warning
  const handleCancelWarning = () => {
    setShowWarning(false);
    setNewSlug(currentSlug); // Reset to current slug
  };

  // Handle confirm warning
  const handleConfirmWarning = () => {
    performSave(newSlug.trim());
  };

  // Save custom bio
  const handleSaveBio = async () => {
    setIsSavingBio(true);
    setBioError('');
    setBioSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/link-in-bio-bio`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ linkInBioBio: newBio.trim() || null })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save custom bio');
      }

      setCurrentBio(newBio.trim());
      setBioSuccessMessage('Your custom bio has been saved!');
      setTimeout(() => setBioSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving custom bio:', err);
      if (isTimeoutError(err)) {
        setBioError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setBioError('Unable to connect to the server.');
      } else {
        setBioError(err.message);
      }
    } finally {
      setIsSavingBio(false);
    }
  };

  // Save service visibility settings
  const handleSaveServiceVisibility = async () => {
    setIsSavingServices(true);
    setServicesError('');
    setServicesSuccessMessage('');

    try {
      // Build array of service visibility updates
      const updates = Object.entries(serviceVisibility).map(([serviceId, showOnLinkInBio]) => ({
        serviceId,
        showOnLinkInBio
      }));

      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/services/visibility`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ serviceVisibility: updates })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save service visibility');
      }

      setServicesSuccessMessage('Service visibility settings saved!');
      setTimeout(() => setServicesSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving service visibility:', err);
      if (isTimeoutError(err)) {
        setServicesError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setServicesError('Unable to connect to the server.');
      } else {
        setServicesError(err.message);
      }
    } finally {
      setIsSavingServices(false);
    }
  };

  // Toggle service visibility
  const toggleServiceVisibility = (serviceId) => {
    setServiceVisibility(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Handle drag end for service reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setOrderedServices((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Save service order
  const handleSaveServiceOrder = async () => {
    setIsSavingOrder(true);
    setOrderError('');
    setOrderSuccessMessage('');

    try {
      const serviceOrder = orderedServices.map((service, index) => ({
        serviceId: service.id,
        displayOrder: index
      }));

      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/services/order`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ serviceOrder })
        },
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to save service order');
      }

      setOrderSuccessMessage('Service order saved successfully!');
      setTimeout(() => setOrderSuccessMessage(''), 3000);

      // Refresh services to ensure they're in sync
      await fetchSlug();
    } catch (error) {
      console.error('Error saving service order:', error);
      if (isTimeoutError(error)) {
        setOrderError('Request timed out. Please try again.');
      } else if (isNetworkError(error)) {
        setOrderError('Unable to connect to the server.');
      } else {
        setOrderError(error.message);
      }
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async () => {
    const url = currentSlug
      ? `${FRONTEND_URL}/${currentSlug}`
      : `${FRONTEND_URL}/doctor/${doctorId}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Validate slug format - returns error message or null if valid
  const validateSlugFormat = (value) => {
    if (!value) return null; // Empty is fine, we'll check for that separately

    // Check for uppercase letters
    if (/[A-Z]/.test(value)) {
      return 'Uppercase letters are not allowed. Please use lowercase only.';
    }

    // Check for spaces
    if (/\s/.test(value)) {
      return 'Spaces are not allowed. Use hyphens (-) instead.';
    }

    // Check for special characters (anything other than lowercase, numbers, hyphens)
    if (/[^a-z0-9-]/.test(value)) {
      return 'Only lowercase letters, numbers, and hyphens are allowed.';
    }

    // Check for consecutive hyphens
    if (/--/.test(value)) {
      return 'Consecutive hyphens are not allowed.';
    }

    // Check if starts or ends with hyphen
    if (value.startsWith('-') || value.endsWith('-')) {
      return 'Slug cannot start or end with a hyphen.';
    }

    // Check minimum length
    if (value.length < 3) {
      return 'Slug must be at least 3 characters long.';
    }

    // Check maximum length
    if (value.length > 50) {
      return 'Slug must be at most 50 characters long.';
    }

    return null; // Valid
  };

  // Handle slug input - don't auto-correct, show validation errors instead
  const handleSlugChange = (e) => {
    const value = e.target.value;
    setNewSlug(value);

    // Validate and show format error
    const formatError = validateSlugFormat(value);
    setSlugFormatError(formatError);

    // Clear availability status when format is invalid
    if (formatError) {
      setSlugAvailable(null);
      setSlugError('');
    }
  };

  // Handle reviews visibility toggle
  const handleToggleReviews = async (newValue) => {
    setIsSavingReviews(true);
    setReviewsError('');
    setReviewsSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/show-reviews`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ showReviews: newValue })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update reviews visibility');
      }

      setShowReviews(newValue);
      setReviewsSuccessMessage(
        newValue
          ? 'Reviews section is now visible on your link-in-bio page'
          : 'Reviews section is now hidden from your link-in-bio page'
      );
      setTimeout(() => setReviewsSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error updating reviews visibility:', err);
      if (isTimeoutError(err)) {
        setReviewsError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setReviewsError('Unable to connect to the server.');
      } else {
        setReviewsError(err.message);
      }
      // Revert the toggle on error
      setShowReviews(!newValue);
    } finally {
      setIsSavingReviews(false);
    }
  };

  // Handle bio visibility toggle
  const handleToggleBio = async (newValue) => {
    setIsSavingBioVisibility(true);
    setBioVisibilityError('');
    setBioVisibilitySuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/show-bio`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ showBio: newValue })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update bio visibility');
      }

      setShowBio(newValue);
      setBioVisibilitySuccessMessage(
        newValue
          ? 'Bio section is now visible on your link-in-bio page'
          : 'Bio section is now hidden from your link-in-bio page'
      );
      setTimeout(() => setBioVisibilitySuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error updating bio visibility:', err);
      if (isTimeoutError(err)) {
        setBioVisibilityError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setBioVisibilityError('Unable to connect to the server.');
      } else {
        setBioVisibilityError(err.message);
      }
      // Revert the toggle on error
      setShowBio(!newValue);
    } finally {
      setIsSavingBioVisibility(false);
    }
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  const previewUrl = newSlug
    ? `${FRONTEND_URL}/${newSlug}`
    : `${FRONTEND_URL}/doctor/${doctorId}`;

  return (
    <div className="flex">
      <DoctorSidebar />
      <div className="flex-1 min-h-[calc(100vh-5rem)] px-4 py-8">
        <div className="max-w-3xl mx-auto">
        {/* Header with breadcrumb */}
        <div className="mb-8">
          <Link
            to="/doctor/dashboard"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Link-in-Bio Settings
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Create a custom, memorable URL for your booking page
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-body">{error}</p>
              <button
                onClick={fetchSlug}
                className="mt-2 font-mono text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-smooth"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
            <Check className="w-5 h-5 text-green-400" />
            <p className="text-green-400 font-body">{successMessage}</p>
          </div>
        )}

        {/* Current URL Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Your Booking Link
          </h2>

          <div className="flex items-center gap-3 p-4 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-xl">
            <div className="flex-1 overflow-hidden">
              <p className="font-mono text-sm text-light-text dark:text-pure-white truncate">
                {currentSlug ? (
                  <>
                    <span className="text-slate-400 dark:text-silver/60">{FRONTEND_URL}/</span>
                    <span className="text-light-text dark:text-pure-white font-medium">{currentSlug}</span>
                  </>
                ) : (
                  <>
                    <span className="text-slate-400 dark:text-silver/60">{FRONTEND_URL}/doctor/</span>
                    <span className="text-light-text dark:text-pure-white font-medium">{doctorId}</span>
                  </>
                )}
              </p>
            </div>
            <button
              onClick={copyToClipboard}
              className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-green-400" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
            >
              <Eye className="w-3 h-3" />
              Preview
            </button>
            <a
              href={currentSlug ? `/${currentSlug}` : `/doctor/${doctorId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
            >
              <ExternalLink className="w-3 h-3" />
              Open
            </a>
          </div>

          {!currentSlug && (
            <p className="mt-3 font-body text-xs text-slate-400 dark:text-silver/40">
              You're using the default URL. Create a custom slug below for a more memorable link.
            </p>
          )}
        </div>

        {/* Custom Slug Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Custom URL Slug
          </h2>

          <div className="space-y-4">
            {/* Slug input */}
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                Your Custom Slug
              </label>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-400 dark:text-silver/60">
                  {FRONTEND_URL}/
                </span>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newSlug}
                    onChange={handleSlugChange}
                    placeholder="dr-sarah-jones"
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                  />
                  {isChecking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-silver/60" />
                    </div>
                  )}
                  {!isChecking && !slugFormatError && slugAvailable === true && newSlug !== currentSlug && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                  )}
                  {!isChecking && (slugFormatError || slugAvailable === false) && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    </div>
                  )}
                </div>
              </div>
              {slugFormatError && (
                <p className="mt-2 font-body text-xs text-red-400">{slugFormatError}</p>
              )}
              {!slugFormatError && slugError && (
                <p className="mt-2 font-body text-xs text-red-400">{slugError}</p>
              )}
              {!slugFormatError && slugAvailable === true && newSlug !== currentSlug && (
                <p className="mt-2 font-body text-xs text-green-400">This slug is available!</p>
              )}
            </div>

            {/* Preview */}
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                Preview URL
              </label>
              <p className="font-mono text-sm text-light-text dark:text-pure-white">
                {previewUrl}
              </p>
            </div>

            {/* Guidelines */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-blue-200 mb-2">
                  <strong>URL Slug Guidelines:</strong>
                </p>
                <ul className="font-body text-xs text-blue-200/80 space-y-1 list-disc list-inside">
                  <li>Only lowercase letters, numbers, and hyphens allowed</li>
                  <li>Must be 3-50 characters long</li>
                  <li>Must be unique across all doctors and clinics</li>
                  <li>Example: <span className="font-mono">dr-sarah-jones</span> or <span className="font-mono">london-dentist-sarah</span></li>
                </ul>
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving || isChecking || (slugAvailable === false) || !newSlug || newSlug === currentSlug || !!slugFormatError}
                className="flex items-center gap-2 px-6 py-2 bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian font-mono text-xs uppercase tracking-widest rounded-lg hover:opacity-90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Bio Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Custom Bio for Link-in-Bio Page
          </h2>

          {bioError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 font-body">{bioError}</p>
            </div>
          )}

          {bioSuccessMessage && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-body">{bioSuccessMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                Custom Bio (Optional)
              </label>
              <textarea
                value={newBio}
                onChange={(e) => setNewBio(e.target.value)}
                placeholder="Write a short bio specifically for your link-in-bio page..."
                rows={5}
                className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-body text-sm focus:border-silver/50 focus:outline-none transition-smooth resize-none"
              />
              <p className="mt-2 font-body text-xs text-slate-400 dark:text-silver/40">
                This bio will be displayed on your link-in-bio page. If left empty, your main profile bio will be shown instead.
              </p>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-blue-200">
                  <strong>Why use a custom bio?</strong>
                </p>
                <p className="font-body text-xs text-blue-200/80 mt-1">
                  Your link-in-bio page is designed for social media and external sharing. You might want a shorter, more casual bio for these contexts versus your full professional bio on the marketplace.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveBio}
                disabled={isSavingBio || newBio === currentBio}
                className="flex items-center gap-2 px-6 py-2 bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian font-mono text-xs uppercase tracking-widest rounded-lg hover:opacity-90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingBio ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Bio'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Reviews Visibility Toggle Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Reviews Section Visibility
          </h2>

          {reviewsError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 font-body">{reviewsError}</p>
            </div>
          )}

          {reviewsSuccessMessage && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-body">{reviewsSuccessMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block font-body text-sm text-light-text dark:text-pure-white mb-1">
                  Show Reviews on Link-in-Bio Page
                </label>
                <p className="font-body text-xs text-slate-400 dark:text-silver/40">
                  Toggle whether patient reviews are displayed on your link-in-bio page. Your average rating will still be visible.
                </p>
              </div>
              <button
                onClick={() => handleToggleReviews(!showReviews)}
                disabled={isSavingReviews}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-obsidian ${
                  showReviews ? 'bg-blue-600' : 'bg-gray-300 dark:bg-charcoal'
                } ${isSavingReviews ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="reviews-visibility-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showReviews ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-blue-200">
                  <strong>Currently {showReviews ? 'visible' : 'hidden'}</strong>
                </p>
                <p className="font-body text-xs text-blue-200/80 mt-1">
                  {showReviews
                    ? 'Patient reviews are displayed on your link-in-bio page, helping build trust with new patients.'
                    : 'Patient reviews are hidden from your link-in-bio page. Only your average rating and review count will be shown.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bio Visibility Toggle Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Bio Section Visibility
          </h2>

          {bioVisibilityError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 font-body">{bioVisibilityError}</p>
            </div>
          )}

          {bioVisibilitySuccessMessage && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-body">{bioVisibilitySuccessMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block font-body text-sm text-light-text dark:text-pure-white mb-1">
                  Show Bio on Link-in-Bio Page
                </label>
                <p className="font-body text-xs text-slate-400 dark:text-silver/40">
                  Toggle whether your bio is displayed on your link-in-bio page. Hiding the bio can create a cleaner, more focused booking experience.
                </p>
              </div>
              <button
                onClick={() => handleToggleBio(!showBio)}
                disabled={isSavingBioVisibility}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-obsidian ${
                  showBio ? 'bg-blue-600' : 'bg-gray-300 dark:bg-charcoal'
                } ${isSavingBioVisibility ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="bio-visibility-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showBio ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-blue-200">
                  <strong>Currently {showBio ? 'visible' : 'hidden'}</strong>
                </p>
                <p className="font-body text-xs text-blue-200/80 mt-1">
                  {showBio
                    ? 'Your bio section is displayed on your link-in-bio page.'
                    : 'Your bio section is hidden from your link-in-bio page. Only your photo, name, specialty, and services will be shown.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Visibility Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Service Visibility
          </h2>

          {servicesError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 font-body">{servicesError}</p>
            </div>
          )}

          {servicesSuccessMessage && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-body">{servicesSuccessMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
              Choose which services to display on your link-in-bio page. Unchecked services will be hidden from public view but remain active in your account.
            </p>

            {services.length === 0 ? (
              <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <Info className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body text-sm text-yellow-200">
                    You haven't created any services yet. Visit the Services page to add your first service.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className="flex items-start gap-3 p-4 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-xl"
                    >
                      <input
                        type="checkbox"
                        id={`service-${service.id}`}
                        checked={serviceVisibility[service.id] ?? true}
                        onChange={() => toggleServiceVisibility(service.id)}
                        className="mt-1 h-5 w-5 rounded border-gray-300 dark:border-border text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-obsidian cursor-pointer"
                      />
                      <label
                        htmlFor={`service-${service.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="font-body text-sm text-light-text dark:text-pure-white font-medium">
                          {service.name}
                        </div>
                        <div className="font-mono text-xs text-slate-400 dark:text-silver/60 mt-1">
                          {service.currency}{service.price} · {service.durationMinutes} minutes
                        </div>
                        {service.description && (
                          <div className="font-body text-xs text-slate-400 dark:text-silver/60 mt-1">
                            {service.description}
                          </div>
                        )}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-blue-200">
                      <strong>Tip:</strong> Only show services that are most relevant for your link-in-bio audience.
                    </p>
                    <p className="font-body text-xs text-blue-200/80 mt-1">
                      You can always change this later. Hidden services remain active and can still be booked through your marketplace profile.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveServiceVisibility}
                    disabled={isSavingServices}
                    className="flex items-center gap-2 px-6 py-2 bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian font-mono text-xs uppercase tracking-widest rounded-lg hover:opacity-90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingServices ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Service Reordering Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Service Display Order
          </h2>

          {orderError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 font-body">{orderError}</p>
            </div>
          )}

          {orderSuccessMessage && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-body">{orderSuccessMessage}</p>
            </div>
          )}

          <div className="space-y-4">
            <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
              Drag and drop to reorder how services appear on your link-in-bio page.
            </p>

            {services.length === 0 ? (
              <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <Info className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body text-sm text-yellow-200">
                    You haven't created any services yet. Visit the Services page to add your first service.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedServices.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {orderedServices.map((service) => (
                        <SortableServiceItem key={service.id} service={service} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-blue-200">
                      <strong>Tip:</strong> Services will appear in this order on your link-in-bio page.
                    </p>
                    <p className="font-body text-xs text-blue-200/80 mt-1">
                      The first service in the list will be shown at the top of your booking page.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveServiceOrder}
                    disabled={isSavingOrder}
                    className="flex items-center gap-2 px-6 py-2 bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian font-mono text-xs uppercase tracking-widest rounded-lg hover:opacity-90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingOrder ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Order'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            How It Works
          </h2>
          <div className="space-y-3 font-body text-sm text-light-text-secondary dark:text-silver/60">
            <p>
              <strong className="text-light-text dark:text-pure-white">Share your link:</strong> Give patients a simple, memorable URL to book appointments with you.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">Social media friendly:</strong> Perfect for Instagram, Twitter, LinkedIn bios, or your email signature.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">Professional branding:</strong> A custom URL looks more professional than a long ID-based link.
            </p>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass border border-light-border dark:border-border rounded-2xl w-full max-w-7xl h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-border">
                <div className="flex items-center gap-4">
                  <Eye className="w-6 h-6 text-light-text dark:text-pure-white" />
                  <h2 className="font-display text-2xl italic tracking-tighter">
                    Preview Link-in-Bio Page
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  {/* View mode toggle */}
                  <div className="flex items-center gap-2 p-1 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded font-mono text-xs uppercase tracking-widest transition-smooth ${
                        previewMode === 'desktop'
                          ? 'bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian'
                          : 'text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white'
                      }`}
                    >
                      <Monitor className="w-4 h-4" />
                      Desktop
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded font-mono text-xs uppercase tracking-widest transition-smooth ${
                        previewMode === 'mobile'
                          ? 'bg-light-text dark:bg-pure-white text-light-bg dark:text-obsidian'
                          : 'text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white'
                      }`}
                    >
                      <Smartphone className="w-4 h-4" />
                      Mobile
                    </button>
                  </div>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-charcoal rounded-lg transition-smooth"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview Info */}
              <div className="px-6 py-3 bg-blue-500/10 border-b border-blue-500/30">
                <p className="font-body text-sm text-blue-200 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  This is a live preview showing how your page will look with your current settings. Changes are not saved until you click the Save buttons.
                </p>
              </div>

              {/* Preview Content */}
              <div className="flex-1 p-6 overflow-hidden">
                <div className={`mx-auto h-full bg-white dark:bg-obsidian rounded-xl shadow-2xl overflow-hidden ${
                  previewMode === 'mobile' ? 'max-w-[375px]' : 'w-full'
                }`}>
                  <iframe
                    src={newSlug ? `/${newSlug}` : `/doctor/${doctorId}`}
                    className="w-full h-full border-0"
                    title="Link-in-Bio Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning Modal */}
        {showWarning && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-2xl italic tracking-tighter mb-2">
                    Warning: Old Links Will Break
                  </h3>
                  <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                    Changing your URL slug will break all existing links that use your old slug.
                  </p>
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="font-body text-xs text-yellow-200">
                      <strong>Current:</strong> <span className="font-mono">{FRONTEND_URL}/{currentSlug}</span>
                    </p>
                    <p className="font-body text-xs text-yellow-200 mt-1">
                      <strong>New:</strong> <span className="font-mono">{FRONTEND_URL}/{newSlug}</span>
                    </p>
                  </div>
                  <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-3">
                    Anyone who bookmarked or shared your old link will need to be notified of the new URL.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelWarning}
                  className="px-4 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmWarning}
                  className="px-4 py-2 bg-yellow-500 text-obsidian rounded-lg font-mono text-xs uppercase tracking-widest hover:bg-yellow-400 transition-smooth"
                >
                  I Understand, Change It
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default DoctorLinkInBio;
